/**
 * ClesRemiseSelect — « quelles clés je donne ? », item 9.2.
 *
 * ─── POURQUOI UN COMPOSANT PARTAGÉ ──────────────────────────────────────────
 *
 * La remise se fait depuis DEUX écrans : la GRC quand l'administré passe à
 * l'accueil chercher les clés et déposer son chèque, le Service Technique quand
 * un agent est sur place et donne le trousseau de la main à la main. Le geste
 * est le même, la règle métier aussi ; deux implémentations divergeraient au
 * premier correctif.
 *
 * ─── CE QUE LE COMPOSANT NE FAIT PAS ────────────────────────────────────────
 *
 * Il ne bloque jamais. Une commune qui n'a pas encore déclaré ses clés doit
 * pouvoir remettre une salle un vendredi soir : le composant signale le manque
 * et propose d'aller le combler, puis laisse valider à vide. Le registre des
 * clés est un outil, pas un péage.
 */
import { useEffect, useState } from 'react';
import { KeyRound, TriangleAlert, Check } from 'lucide-react';
import { api, type ClesReservationVue, type CleDisponibleDTO } from '@/lib/api';

interface Props {
  reservationId: string;
  /** Remonte la sélection au parent, qui l'envoie avec le changement de statut. */
  onChange: (cleIds: string[]) => void;
  /** Affiché sous le titre. Contexte différent selon l'écran appelant. */
  aide?: string;
}

/** « Porte principale · exemplaire 2 sur 3 · n° A-12 » */
function libelle(c: CleDisponibleDTO): string {
  const parts = [c.porte, `exemplaire ${c.exemplaire}`];
  if (c.numeroGrave) parts.push(`n° ${c.numeroGrave}`);
  return parts.join(' · ');
}

export function ClesRemiseSelect({ reservationId, onChange, aide }: Props) {
  const [vue, setVue] = useState<ClesReservationVue | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [choisies, setChoisies] = useState<string[]>([]);

  useEffect(() => {
    let vivant = true;
    api.materiel.reservations.cles(reservationId)
      .then((v) => {
        if (!vivant) return;
        setVue(v);
        // Les clés déjà sorties AU TITRE de cette réservation sont cochées
        // d'office : le second écran voit ce que le premier a fait.
        const deja = v.cles.filter((c) => c.pourCetteReservation).map((c) => c.cleId);
        setChoisies(deja);
        onChange(deja);
      })
      .catch((e: unknown) => {
        if (vivant) setErreur(e instanceof Error ? e.message : 'Lecture des clés impossible.');
      });
    return () => { vivant = false; };
    // `onChange` est recréé à chaque rendu du parent : l'inclure relancerait
    // l'appel en boucle. La réservation seule détermine ce qu'on lit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId]);

  function basculer(cleId: string) {
    setChoisies((prev) => {
      const suivant = prev.includes(cleId)
        ? prev.filter((x) => x !== cleId)
        : [...prev, cleId];
      onChange(suivant);
      return suivant;
    });
  }

  if (erreur) {
    return (
      <p className="text-xs text-ink-subtle">
        Clés indisponibles ({erreur}). La remise reste possible.
      </p>
    );
  }
  if (!vue) return <p className="text-xs text-ink-subtle">Lecture des clés…</p>;

  // Réservation de matériel, ou salle jamais rattachée : pas de bloc du tout.
  if (!vue.lieuRattache) {
    return (
      <p className="flex items-start gap-1.5 text-xs text-ink-subtle">
        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Cette salle n’est rattachée à aucun lieu : impossible de désigner une clé.
        Rattachez-la depuis sa fiche pour en tracer la remise.
      </p>
    );
  }

  if (vue.cles.length === 0) {
    return (
      <p className="flex items-start gap-1.5 text-xs text-ink-subtle">
        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Aucune clé déclarée pour {vue.lieuNom ?? 'ce lieu'}. Vous pouvez remettre la
        salle quand même — pensez à déclarer les clés depuis la fiche du lieu.
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-border/60 p-3">
      <p className="flex items-center gap-1.5 text-sm font-medium text-ink">
        <KeyRound className="h-4 w-4" aria-hidden="true" />
        Clés remises
      </p>
      {aide && <p className="mt-0.5 text-xs text-ink-subtle">{aide}</p>}

      <ul className="mt-2 space-y-1">
        {vue.cles.map((c) => {
          // Sortie pour quelqu'un d'AUTRE : on la montre, on ne la propose pas.
          // La masquer ferait croire qu'elle n'existe pas, alors que la vraie
          // information utile est « elle est chez untel ».
          const indisponible =
            (c.sortie && !c.pourCetteReservation)
            || c.etat === 'perdue' || c.etat === 'detruite';
          const coche = choisies.includes(c.cleId);
          return (
            <li key={c.cleId}>
              <label
                className={`flex items-center gap-2 text-sm ${
                  indisponible ? 'text-ink-subtle' : 'cursor-pointer text-ink'
                }`}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border"
                  checked={coche}
                  disabled={indisponible}
                  onChange={() => basculer(c.cleId)}
                />
                <span>{libelle(c)}</span>
                {c.pourCetteReservation && (
                  <span className="flex items-center gap-1 text-xs text-success-fg">
                    <Check className="h-3 w-3" aria-hidden="true" /> déjà remise
                  </span>
                )}
                {c.etat === 'perdue' && <span className="text-xs">— déclarée perdue</span>}
                {c.etat === 'detruite' && <span className="text-xs">— détruite</span>}
                {c.sortie && !c.pourCetteReservation && (
                  <span className="text-xs">
                    — sortie{c.detenueParEmprunteur ? ` (${c.detenueParEmprunteur})` : ''}
                  </span>
                )}
              </label>
            </li>
          );
        })}
      </ul>

      {vue.remise.remises && (
        <p className="mt-2 text-xs text-ink-subtle">
          {vue.remise.nbCles > 1
            ? `${vue.remise.nbCles} clés déjà remises`
            : 'Clé déjà remise'}
          {vue.remise.parNom ? ` par ${vue.remise.parNom}` : ''}
          {vue.remise.remiseLe ? ` le ${vue.remise.remiseLe.split('-').reverse().join('/')}` : ''}.
        </p>
      )}
    </div>
  );
}
